import type { AxiosInstance } from 'axios'
import type { ApiItem, ApiList } from '@yuksalish/domain-types'
import type {
  EngineeringProduct,
  BomVersion,
  BomVersionSummary,
  BomVersionPayload,
  BomLine,
  BomOperation,
  CatalogKind,
  ProductFilters,
  ProductGroupOption,
  ProductPayload,
  ProductAudienceOption,
  ProductSeasonOption,
  ProductUom,
  ProductionReadiness,
  TechnicalPassport,
  TechnicalPassportDictionaries,
  TechnicalPassportPayload,
  TechnicalPassportMaterialRequirements,
  EquipmentReference,
  OperationReference,
  ExpenseReference,
  ColorReference,
  UnitReference,
  ThreadCodeReference,
  DepartmentOption,
  AgentScopeOption,
  ProductionOrder,
  ProductionOrderPayload,
  ProductionOrderPage,
  ProductionDailyOutput,
  ProductionOrderStage,
  ProductionLot,
  ProductionEmployee,
  ReferenceItemPayload,
  ReferenceItem,
  SalesOrderOption,
  CostingComponentInput,
  CostingResult,
  CostCalculationSnapshot,
} from './types'
import {
  catalogTypeForKind,
  MANUFACTURED_PRODUCT_TYPES,
} from './productPresentation'

function unwrapList<T>(payload: unknown): T[] {
  const value = payload as {
    data?: unknown
    items?: unknown
  }

  if (Array.isArray(payload)) return payload as T[]
  if (Array.isArray(value?.data)) return value.data as T[]

  const nestedData = value?.data as { items?: unknown } | undefined
  if (Array.isArray(nestedData?.items)) return nestedData.items as T[]
  if (Array.isArray(value?.items)) return value.items as T[]

  return []
}

export async function listProducts(
  client: AxiosInstance,
  filters: ProductFilters,
  includeReadiness = true,
): Promise<EngineeringProduct[]> {
  const response = await client.get<ApiList<EngineeringProduct>>('/products', {
    params: {
      ...filters,
      ...(includeReadiness ? { include_model_status: 1 } : {}),
    },
  })
  return response.data.data
}

export async function listCatalogProducts(
  client: AxiosInstance,
  kind: CatalogKind,
  filters: ProductFilters,
): Promise<EngineeringProduct[]> {
  const fixedType = catalogTypeForKind(kind)
  if (fixedType) {
    return listProducts(
      client,
      { ...filters, type: fixedType },
      false,
    )
  }

  const selectedType = filters.type
  const productTypes = selectedType
    ? [selectedType]
    : [...MANUFACTURED_PRODUCT_TYPES]
  const results = await Promise.all(
    productTypes.map((type) =>
      listProducts(client, { ...filters, type }, true),
    ),
  )
  return results.flat().sort((left, right) => right.id - left.id)
}

export async function listProductGroups(
  client: AxiosInstance,
): Promise<ProductGroupOption[]> {
  const response = await client.get<ApiList<ProductGroupOption>>('/product-groups', {
    params: { page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function listProductAudiences(
  client: AxiosInstance,
): Promise<ProductAudienceOption[]> {
  const response = await client.get<ApiList<ProductAudienceOption>>('/product-audiences', {
    params: { is_active: 1 },
  })
  return unwrapList<ProductAudienceOption>(response.data)
}

export async function listAllProductAudiences(
  client: AxiosInstance,
): Promise<ProductAudienceOption[]> {
  const response = await client.get<ApiList<ProductAudienceOption>>('/product-audiences', {
    params: { page: 1, per_page: 500 },
  })
  return unwrapList<ProductAudienceOption>(response.data)
}

export async function createProductAudience(
  client: AxiosInstance,
  payload: Pick<ProductAudienceOption, 'code' | 'name' | 'is_active'>,
): Promise<ProductAudienceOption> {
  const response = await client.post<ApiItem<ProductAudienceOption>>('/product-audiences', payload)
  return response.data.data
}

export async function updateProductAudience(
  client: AxiosInstance,
  audienceId: number,
  payload: Pick<ProductAudienceOption, 'code' | 'name' | 'is_active'>,
): Promise<ProductAudienceOption> {
  const response = await client.put<ApiItem<ProductAudienceOption>>(`/product-audiences/${audienceId}`, payload)
  return response.data.data
}

export async function deleteProductAudience(
  client: AxiosInstance,
  audienceId: number,
): Promise<void> {
  await client.delete(`/product-audiences/${audienceId}`)
}

export async function listProductSeasons(
  client: AxiosInstance,
): Promise<ProductSeasonOption[]> {
  const response = await client.get<ApiList<ProductSeasonOption>>('/product-seasons', {
    params: { is_active: 1 },
  })
  return unwrapList<ProductSeasonOption>(response.data)
}

export async function listAllProductSeasons(
  client: AxiosInstance,
): Promise<ProductSeasonOption[]> {
  const response = await client.get<ApiList<ProductSeasonOption>>('/product-seasons', {
    params: { page: 1, per_page: 500 },
  })
  return unwrapList<ProductSeasonOption>(response.data)
}

export async function createProductSeason(
  client: AxiosInstance,
  payload: Pick<ProductSeasonOption, 'code' | 'name' | 'is_active'>,
): Promise<ProductSeasonOption> {
  const response = await client.post<ApiItem<ProductSeasonOption>>('/product-seasons', payload)
  return response.data.data
}

export async function updateProductSeason(
  client: AxiosInstance,
  seasonId: number,
  payload: Pick<ProductSeasonOption, 'code' | 'name' | 'is_active'>,
): Promise<ProductSeasonOption> {
  const response = await client.put<ApiItem<ProductSeasonOption>>(`/product-seasons/${seasonId}`, payload)
  return response.data.data
}

export async function deleteProductSeason(
  client: AxiosInstance,
  seasonId: number,
): Promise<void> {
  await client.delete(`/product-seasons/${seasonId}`)
}

export async function listProductBom(
  client: AxiosInstance,
  productId: number,
): Promise<BomLine[]> {
  const response = await client.get<ApiList<BomLine>>('/bom', {
    params: { product_id: productId, page: 1, per_page: 100 },
  })
  return response.data.data
}

export async function listProductBomVersions(
  client: AxiosInstance,
  productId: number,
): Promise<BomVersionSummary[]> {
  const response = await client.get<ApiList<BomVersionSummary>>('/bom', {
    params: { product_id: productId, page: 1, per_page: 100 },
  })
  return response.data.data
}

export async function getBomVersion(
  client: AxiosInstance,
  versionId: number,
): Promise<BomVersion> {
  const response = await client.get<ApiItem<BomVersion>>(`/bom/${versionId}`)
  return response.data.data
}

export async function createBomVersion(
  client: AxiosInstance,
  payload: BomVersionPayload,
): Promise<BomVersion> {
  const response = await client.post<ApiItem<BomVersion>>('/bom', payload)
  return response.data.data
}

export async function updateBomVersion(
  client: AxiosInstance,
  versionId: number,
  payload: BomVersionPayload,
): Promise<BomVersion> {
  const response = await client.put<ApiItem<BomVersion>>(`/bom/${versionId}`, payload)
  return response.data.data
}

export async function listOperationReferences(
  client: AxiosInstance,
): Promise<OperationReference[]> {
  const response = await client.get<ApiList<OperationReference>>(
    '/reference-items/operation',
    { params: { page: 1, per_page: 500 } },
  )
  return response.data.data
}

export async function listExpenseReferences(
  client: AxiosInstance,
): Promise<ExpenseReference[]> {
  const response = await client.get<ApiList<ExpenseReference>>(
    '/reference-items/expense_type',
    { params: { page: 1, per_page: 500 } },
  )
  return response.data.data
}

export async function listReferenceItems(
  client: AxiosInstance,
  type: 'operation' | 'expense_type',
): Promise<OperationReference[] | ExpenseReference[]> {
  const response = await client.get<
    ApiList<OperationReference> | ApiList<ExpenseReference>
  >(`/reference-items/${type}`, {
    params: { is_active: 1, page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function listDepartmentOptions(
  client: AxiosInstance,
): Promise<DepartmentOption[]> {
  const response = await client.get<ApiList<DepartmentOption>>('/agents', {
    params: { role: 'department', is_active: 1, page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function listAgentScopeOptions(
  client: AxiosInstance,
  role: 'department' | 'warehouse',
): Promise<AgentScopeOption[]> {
  const response = await client.get<ApiList<AgentScopeOption> | { data: AgentScopeOption[] }>('/agents', {
    params: { role, is_active: 1 },
  })
  return response.data.data
}

export async function listPersonnelEmployees(
  client: AxiosInstance,
  search?: string,
): Promise<ProductionEmployee[]> {
  const response = await client.get<ApiList<ProductionEmployee>>('/employees', {
    params: { search: search || undefined, is_active: 1, page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function listSalesOrders(
  client: AxiosInstance,
  search?: string,
): Promise<SalesOrderOption[]> {
  const response = await client.get<ApiList<SalesOrderOption>>('/orders', {
    params: { search: search || undefined, page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function listProductBomOperations(
  client: AxiosInstance,
  productId: number,
): Promise<BomOperation[]> {
  const response = await client.get<ApiList<BomOperation>>('/bom/operations', {
    params: { product_id: productId },
  })
  return response.data.data
}

export async function getTechnicalPassport(
  client: AxiosInstance,
  passportId: number,
): Promise<TechnicalPassport> {
  const response = await client.get<ApiItem<TechnicalPassport>>(
    `/technical-passports/${passportId}`,
  )
  return response.data.data
}

export async function getTechnicalPassportDictionaries(
  client: AxiosInstance,
): Promise<TechnicalPassportDictionaries> {
  const response = await client.get<ApiItem<TechnicalPassportDictionaries>>(
    '/technical-passports/dictionaries',
  )
  return response.data.data
}

export async function listEquipmentReferences(
  client: AxiosInstance,
): Promise<EquipmentReference[]> {
  const response = await client.get<ApiList<EquipmentReference>>(
    '/reference-items/equipment',
    { params: { is_active: 1 } },
  )
  return response.data.data
}

export async function listColorReferences(
  client: AxiosInstance,
): Promise<ColorReference[]> {
  const response = await client.get<ApiList<ColorReference>>(
    '/reference-items/color',
    { params: { is_active: 1 } },
  )
  return response.data.data
}

export async function listUnitReferences(
  client: AxiosInstance,
): Promise<UnitReference[]> {
  const response = await client.get<ApiList<UnitReference>>(
    '/reference-items/unit',
    { params: { is_active: 1 } },
  )
  return response.data.data
}

export async function listThreadCodes(
  client: AxiosInstance,
): Promise<ThreadCodeReference[]> {
  const response = await client.get<ApiList<ThreadCodeReference>>('/thread-codes', {
    params: { is_active: 1, page: 1, per_page: 500 },
  })
  return response.data.data
}

export async function createThreadCode(
  client: AxiosInstance,
  payload: {
    code: string
    name: string
    yarn_type?: string
    yarn_composition?: string
    is_active?: number | boolean
    sort_order?: number
  },
): Promise<ThreadCodeReference> {
  const response = await client.post<ApiItem<ThreadCodeReference>>('/thread-codes', payload)
  return response.data.data
}

export async function createReferenceItem(
  client: AxiosInstance,
  type: 'color' | 'unit' | 'equipment' | 'operation' | 'packaging' | 'expense_type',
  payload: ReferenceItemPayload,
): Promise<ReferenceItem> {
  const response = await client.post<ApiItem<ReferenceItem>>(
    `/reference-items/${type}`,
    payload,
  )
  return response.data.data
}

export async function updateReferenceItem(
  client: AxiosInstance,
  type: 'color' | 'unit' | 'equipment' | 'operation' | 'packaging' | 'expense_type',
  id: number,
  payload: ReferenceItemPayload,
): Promise<ReferenceItem> {
  const response = await client.put<ApiItem<ReferenceItem>>(
    `/reference-items/${type}/${id}`,
    payload,
  )
  return response.data.data
}

export async function deleteReferenceItem(
  client: AxiosInstance,
  type: 'color' | 'unit' | 'equipment' | 'operation' | 'packaging' | 'expense_type',
  id: number,
): Promise<void> {
  await client.delete(`/reference-items/${type}/${id}`)
}

export async function createTechnicalPassport(
  client: AxiosInstance,
  payload: TechnicalPassportPayload,
): Promise<TechnicalPassport> {
  const response = await client.post<ApiItem<TechnicalPassport>>(
    '/technical-passports',
    payload,
  )
  return response.data.data
}

export async function updateTechnicalPassport(
  client: AxiosInstance,
  passportId: number,
  payload: Partial<TechnicalPassportPayload>,
): Promise<TechnicalPassport> {
  const response = await client.put<ApiItem<TechnicalPassport>>(
    `/technical-passports/${passportId}`,
    payload,
  )
  return response.data.data
}

export async function duplicateTechnicalPassport(
  client: AxiosInstance,
  passportId: number,
  productId: number,
  overrides: Partial<TechnicalPassportPayload> = {},
): Promise<TechnicalPassport> {
  const response = await client.post<ApiItem<TechnicalPassport>>(
    `/technical-passports/${passportId}/duplicate`,
    { new_product_id: productId, overrides },
  )
  return response.data.data
}

export async function listTechnicalPassports(
  client: AxiosInstance,
  productId: number,
): Promise<TechnicalPassport[]> {
  const response = await client.get<ApiList<TechnicalPassport>>(
    '/technical-passports',
    { params: { product_id: productId } },
  )
  return response.data.data
}

export async function getTechnicalPassportMaterialRequirements(
  client: AxiosInstance,
  passportId: number,
  pairs: number,
): Promise<TechnicalPassportMaterialRequirements> {
  const response = await client.get<
    ApiItem<TechnicalPassportMaterialRequirements>
  >(`/technical-passports/${passportId}/material-requirements`, {
    params: { pairs },
  })
  return response.data.data
}

export async function getProduct(
  client: AxiosInstance,
  productId: number,
): Promise<EngineeringProduct> {
  const response = await client.get<ApiItem<EngineeringProduct>>(
    `/products/${productId}`,
  )
  return response.data.data
}

export async function getProductUoms(
  client: AxiosInstance,
  productId: number,
): Promise<ProductUom[]> {
  const response = await client.get<ApiItem<ProductUom[]> | ApiList<ProductUom>>(
    `/products/${productId}/uoms`,
  )
  return unwrapList<ProductUom>(response.data)
}

export async function replaceProductUoms(
  client: AxiosInstance,
  productId: number,
  uoms: ProductUom[],
): Promise<ProductUom[]> {
  const response = await client.post<ApiItem<ProductUom[]> | ApiList<ProductUom>>(
    `/products/${productId}/uoms`,
    { uoms },
  )
  const saved = unwrapList<ProductUom>(response.data)
  return saved.length ? saved : uoms
}

export async function createProduct(
  client: AxiosInstance,
  payload: ProductPayload,
): Promise<EngineeringProduct> {
  const response = await client.post<ApiItem<EngineeringProduct>>('/products', payload)
  return response.data.data
}

export async function updateProduct(
  client: AxiosInstance,
  productId: number,
  payload: Partial<ProductPayload>,
): Promise<EngineeringProduct> {
  const response = await client.put<ApiItem<EngineeringProduct>>(`/products/${productId}`, payload)
  return response.data.data
}

export async function deleteProduct(
  client: AxiosInstance,
  productId: number,
): Promise<void> {
  await client.delete(`/products/${productId}`)
}

export async function getProductionReadiness(
  client: AxiosInstance,
  productId: number,
): Promise<ProductionReadiness> {
  const response = await client.get<ApiItem<ProductionReadiness>>(
    `/products/${productId}/production-readiness`,
  )
  return response.data.data
}

export async function createProductionOrder(
  client: AxiosInstance,
  payload: ProductionOrderPayload,
): Promise<ProductionOrder> {
  const response = await client.post<ApiItem<ProductionOrder>>(
    '/production-orders',
    payload,
  )
  return response.data.data
}

export async function listProductionOrders(
  client: AxiosInstance,
  filters: { search?: string; status?: string; page: number; per_page: number },
): Promise<ProductionOrderPage> {
  const response = await client.get<ApiList<ProductionOrder>>('/production-orders', { params: filters })
  const meta = response.data.meta
  return {
    items: response.data.data,
    page: meta?.page ?? meta?.current_page ?? filters.page,
    per_page: meta?.per_page ?? filters.per_page,
    total: meta?.total ?? response.data.data.length,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

export async function getProductionDailyOutput(
  client: AxiosInstance,
  date: string,
): Promise<ProductionDailyOutput> {
  const response = await client.get<ApiItem<ProductionDailyOutput>>(
    '/production-execution/daily-output',
    { params: { date } },
  )
  return response.data.data
}

export async function getProductionOrder(client: AxiosInstance, id: number): Promise<ProductionOrder> {
  const response = await client.get<ApiItem<ProductionOrder>>(`/production-orders/${id}`)
  return response.data.data
}

export async function getProductionOrderStages(client: AxiosInstance, id: number): Promise<ProductionOrderStage[]> {
  const response = await client.get<ApiList<ProductionOrderStage>>(`/production-orders/${id}/stages`)
  return response.data.data
}

export async function listProductionLots(client: AxiosInstance, orderId: number): Promise<ProductionLot[]> {
  const response = await client.get<ApiList<ProductionLot>>(`/production-orders/${orderId}/lots`)
  return response.data.data
}
export async function createProductionLot(client: AxiosInstance, orderId: number, payload: { stage_code: string; quantity: number; notes?: string }): Promise<ProductionLot> {
  const response = await client.post<ApiItem<ProductionLot>>(`/production-orders/${orderId}/lots`, payload)
  return response.data.data
}
export async function getProductionLot(client: AxiosInstance, orderId: number, lotNumber: string): Promise<ProductionLot> {
  const response = await client.get<ApiItem<ProductionLot>>(`/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}`)
  return response.data.data
}
export async function postLotStageProgress(client: AxiosInstance, orderId: number, lotNumber: string, stageCode: string, payload: { completed_delta: number; rejected_delta: number; rework_delta: number; employee_id: number; notes?: string }): Promise<ProductionLot> {
  const response = await client.post<ApiItem<ProductionLot>>(`/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/progress`, payload)
  return response.data.data
}
export async function closeLotStage(client: AxiosInstance, orderId: number, lotNumber: string, stageCode: string, payload: { employee_id: number; notes?: string }): Promise<ProductionLot> {
  const response = await client.post<ApiItem<ProductionLot>>(`/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/close`, payload)
  return response.data.data
}

export async function listProductionEmployees(client: AxiosInstance, search?: string): Promise<ProductionEmployee[]> {
  const response = await client.get<ApiList<ProductionEmployee>>('/employees', {
    params: { search: search || undefined, employee_type: 'production', is_active: 1, page: 1, per_page: 100 },
  })
  return response.data.data
}

export async function calculateCosting(
  client: AxiosInstance,
  payload: {
    product_id: number
    production_type?: string
    version_id?: number
    currency?: string
    components: CostingComponentInput[]
  },
): Promise<CostingResult> {
  const response = await client.post<ApiItem<CostingResult>>('/costings/calculate', payload)
  return response.data.data
}

export async function listCostCalculations(
  client: AxiosInstance,
  productId: number,
  filters: { production_type?: string; page?: number; per_page?: number } = {},
): Promise<{ items: CostCalculationSnapshot[]; total: number; page: number; per_page: number; total_pages: number }> {
  const response = await client.get<ApiList<CostCalculationSnapshot>>('/costings', {
    params: { product_id: productId, ...filters },
  })
  const meta = response.data.meta
  const requestedPage = filters.page ?? 1
  const requestedPerPage = filters.per_page ?? 20
  return {
    items: response.data.data,
    total: meta?.total ?? response.data.data.length,
    page: meta?.page ?? meta?.current_page ?? requestedPage,
    per_page: meta?.per_page ?? requestedPerPage,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}
